// The Curator API client, and the one Consumer API call Studio makes: a test
// run. Every Curator API call goes through the contract's own client
// (HttpApiClient over CuratorApi, src/exchange/curator-api.ts), so a response
// and a declared failure are decoded with the contract's schemas and a body
// that doesn't match is an UnreadableResponse, never a value Studio trusts.
// See Design > Exchange, "Curator API" and "Test runs", and Design > Code
// standards, "Decode at the boundary". It sends the signed-in curator's
// bearer token, which the Exchange reads to show them only their own hubs
// and to let them change and test only those hubs.

import { Cause, Effect, Exit, Option, Schema } from "effect";
import { FetchHttpClient, HttpClient, HttpClientRequest, type HttpClientResponse } from "effect/http";
import { HttpApiClient } from "effect/http-api";
import { CuratorApi, type ActionId, type CommitSha, type CuratorReply, type HubId, type InteractionId, type Json, type TestRunId, type TestRunKind } from "../../src/exchange/curator-api.ts";
import { AGENT_ACTIVITY_LIMIT } from "./domain/agent.ts";
import { FailureFields, TestRunResponse, type ApiFailure, type DemandResponse, type EventDetail, type EventsResponse, type GoldenResponse, type StudioHubs } from "./domain/types.ts";

export type Result<T> = { ok: true; value: T } | { ok: false; error: ApiFailure };

/** The writes' and reads' successes, as the contract decodes them. */
type Api_ = HttpApiClient.ForApi<typeof CuratorApi>;
// A client method is generic over its response mode, so `infer` sees the union of all three; only the decoded one is a success Studio reads.
type Out<K extends keyof Api_> = Api_[K] extends (...a: never[]) => Effect.Effect<infer A, unknown, unknown>
  ? Exclude<A, HttpClientResponse.HttpClientResponse | readonly [unknown, HttpClientResponse.HttpClientResponse]> : never;

export type Api = {
  hubs(): Promise<Result<StudioHubs>>;
  events(hub: HubId, limit?: number): Promise<Result<EventsResponse>>;
  demand(hub: HubId, days: number): Promise<Result<DemandResponse>>;
  golden(): Promise<Result<GoldenResponse>>;
  /** Registers a WarmHub repository as a hub: `repo` is `org/name`. */
  register(repo: string): Promise<Result<Out<"registerHub">>>;
  /** Issues a push token for the hub's toolset repository; it is shown once. */
  pushToken(hub: HubId): Promise<Result<Out<"issuePushToken">>>;
  /** The hub's secrets' states, its Hub token's among them, never a value. */
  secrets(hub: HubId): Promise<Result<Out<"listHubSecrets">>>;
  /** Connects the hub's Hub token, minted with the curator's own token, so the hub agent can work. */
  connectHubToken(hub: HubId): Promise<Result<Out<"connectHubToken">>>;
  /** Disconnects the hub's Hub token, so the hub agent stops reading. */
  disconnectHubToken(hub: HubId): Promise<Result<Out<"disconnectHubToken">>>;
  /** Grants the hub's WarmHub token for one toolset, with the curator's own token. */
  grant(hub: HubId, commit: string): Promise<Result<Out<"grantHubSecret">>>;
  toolsets(hub: HubId): Promise<Result<Out<"listToolsets">>>;
  /** The hub's newest 50 toolset activations, rollbacks included, newest first. */
  activations(hub: HubId): Promise<Result<Out<"listActivations">>>;
  /** Stages `main` of the hub's toolset repository, or one commit. Waits for staging to finish, up to 25 seconds. */
  stage(hub: HubId, commit: "main" | CommitSha): Promise<Result<Out<"stageToolset">>>;
  /** Starts a recorded test run and waits for it to end, up to 25 seconds; null is a toolset the hub doesn't have. */
  startRun(hub: HubId, commit: CommitSha, kind: TestRunKind): Promise<Result<Out<"startTestRun">>>;
  run(hub: HubId, runId: TestRunId): Promise<Result<Out<"getTestRun">>>;
  activate(hub: HubId, commit: CommitSha): Promise<Result<Out<"activateToolset">>>;
  /** Asks the hub agent for a repair round of one toolset: a 202 with the hub event the request became. See Design > Hub agent, "Repair rounds". */
  requestRepair(hub: HubId, commit: CommitSha): Promise<Result<Out<"requestRepair">>>;
  list(hub: HubId): Promise<Result<Out<"listHub">>>;
  unlist(hub: HubId, reason?: string): Promise<Result<Out<"unlistHub">>>;
  /** Sets who can find the hub: everyone signed in, or the members of its repository's org. */
  setAudience(hub: HubId, kind: "everyone" | "org"): Promise<Result<Out<"setHubAudience">>>;
  /** Asks one question of one hub as a test run, which charges nothing; `staged` tries its newest staged toolset. */
  testRun(question: string, opts: { hub: string; bid: number; staged: boolean }): Promise<Result<TestRunResponse>>;
  /** The hub agent's newest agent actions, and its Interactions: every open one and the newest closed. */
  agent(hub: HubId, limit?: number): Promise<Result<Out<"getAgentActivity">>>;
  /** One agent action and its curated transcript. */
  agentAction(hub: HubId, actionId: ActionId): Promise<Result<Out<"getAgentAction">>>;
  /**
   * A Curator reply to one Interaction, which the caller has checked with
   * checkReply. One its reply schema refuses is the declared ReplyInvalid,
   * and any reply once it's closed, or a second, different one, the declared
   * InteractionClosed, both a 409; a body that doesn't decode is the empty 400.
   */
  reply(hub: HubId, interactionId: InteractionId, reply: CuratorReply): Promise<Result<Out<"replyToInteraction">>>;
  /** The Interactions waiting on the curator on every hub they curate, oldest first, and the informs they haven't read, newest first. */
  waitingInteractions(): Promise<Result<Out<"listWaitingInteractions">>>;
  /** The curator's read marks on the hub's informs, at most 50: a 204. */
  markRead(hub: HubId, interactionIds: ReadonlyArray<InteractionId>): Promise<Result<Out<"markInteractionsRead">>>;
};

/** A bearer token for one request, or null when there is none to send. */
export type TokenProvider = () => Promise<string | null> | string | null;

/** The parts of an event's detail Studio reads, each only when it has the type Studio shows it as. */
const eventDetailOf = (detail: Json | null): EventDetail | null => {
  if (typeof detail !== "object" || detail === null || Array.isArray(detail)) return null;
  const { price, answerId, reason } = detail;
  return {
    ...(typeof price === "number" ? { price } : {}),
    ...(typeof answerId === "string" ? { answerId } : {}),
    ...(typeof reason === "string" ? { reason } : {}),
  };
};

/** What a failed call knew: the response's status and request ID, when it got one. */
type Seen = { status: number; requestId?: string; body?: unknown };

/** The tags of the HTTP client's and the decoder's own errors, which say the exchange didn't go as the contract has it, not what the Exchange answered. */
const CLIENT_ERRORS = new Set(["HttpClientError", "SchemaError"]);

/**
 * The fields Studio reads from any failure a call can end in. A failure the
 * contract declares is decoded: its tag and authored detail. Any other
 * (a proxy's or a gateway's answer, which the dev server's
 * ExchangeUnreachable is) is read from the response body field by field,
 * because those may send anything.
 */
const readFailureFields = Schema.decodeUnknownOption(FailureFields);
type Fields = typeof FailureFields.Type;
const failureFields = (value: Option.Option<Fields>): Fields => Option.getOrElse(value, (): Fields => ({}));
const failureOf = (error: Option.Option<Fields>, seen: Seen | null, body: Fields = {}): ApiFailure => {
  const status = seen?.status ?? 0;
  if (status === 0) return { _tag: "NetworkError", status: 0, detail: "Studio couldn't reach the Exchange." };
  const requestId = seen?.requestId ? { requestId: seen.requestId } : {};
  const decoded = failureFields(error);
  // A declared failure carries its tag and detail (src/domain/errors.ts); a client or decode error carries neither.
  const declared = status >= 400 && decoded._tag !== undefined && !CLIENT_ERRORS.has(decoded._tag);
  // The only undeclared body whose text Studio shows is its own dev server's: anything else's `detail` isn't text we wrote.
  const e = declared ? decoded : status === 502 && body._tag === "ExchangeUnreachable" ? body : {};
  if (!declared && status < 400) return { _tag: "UnreadableResponse", status, ...requestId, detail: "Studio couldn't read the Exchange's answer." };
  return {
    _tag: e._tag ?? `Http${status}`, status, ...requestId,
    ...(e.detail !== undefined ? { detail: e.detail } : {}), ...(e.reason !== undefined ? { reason: e.reason } : {}),
    ...(e.interaction !== undefined ? { closedAs: e.interaction.state } : {}),
  };
};

export function createApi(base: string, fetchImpl: typeof fetch = (...a) => fetch(...a), token: TokenProvider = () => null): Api {
  // One client per call, so the status and request ID it records belong to this call alone.
  async function curator<A>(use: (api: Api_) => Effect.Effect<A, unknown, never>): Promise<Result<A>> {
    const responses: Seen[] = [];
    const tokenFailed: true[] = [];
    const program = Effect.gen(function* () {
      const api = yield* HttpApiClient.make(CuratorApi, {
        baseUrl: base,
        transformClient: (client) => client.pipe(
          // A token that can't be had stops the call before any request; the failure says so, and the screen asks to sign in again.
          HttpClient.mapRequestEffect((req) => Effect.tryPromise({ try: async () => await token(), catch: () => "TokenUnavailable" as const }).pipe(
            Effect.map((bearer) => (bearer ? HttpClientRequest.setHeader(req, "authorization", `Bearer ${bearer}`) : req)),
            Effect.tapError(() => Effect.sync(() => { tokenFailed.push(true); })),
            Effect.orDie)),
          // The body of a failing response is kept too, for the one answer the contract doesn't declare that Studio owns: its dev server's.
          HttpClient.tap((res) => Effect.gen(function* () {
            const requestId = res.headers["x-request-id"];
            // A body that can't be read is no body: the contract's decode reports the real failure, so none is lost here.
            const body = res.status >= 400 ? Option.getOrUndefined(yield* Effect.option(res.json)) : undefined;
            responses.push({ status: res.status, ...(requestId ? { requestId } : {}), ...(body !== undefined ? { body } : {}) });
          }))),
      });
      return yield* use(api);
    }).pipe(Effect.provide(FetchHttpClient.layer), Effect.provideService(FetchHttpClient.Fetch, fetchImpl),
      // Trace headers would fail the browser's CORS preflight: the Exchange allows authorization and content-type only (src/exchange/cors.ts).
      Effect.provideService(HttpClient.TracerPropagationEnabled, false));
    const exit = await Effect.runPromiseExit(program);
    if (Exit.isSuccess(exit)) return { ok: true, value: exit.value };
    const failure = Cause.findErrorOption(exit.cause);
    const last = responses.at(-1) ?? null;
    // The cause stays in the console with the request's ID, never in the curator's words. See Design > Errors, "Keep the cause for the log".
    // Only tags and the status: a decode failure's text can quote the body, which may hold a push token.
    console.error("studio: a Curator API call failed", { requestId: last?.requestId, status: last?.status, failed: failure._tag === "Some" });
    if (tokenFailed.length > 0) return { ok: false, error: { _tag: "SignInExpired", status: 0, detail: "Studio couldn't refresh your sign-in. Sign in again." } };
    // A defect (a decode that threw) reads as the unreadable body it is.
    const error = failure._tag === "Some" ? readFailureFields(failure.value) : Option.some({ _tag: "SchemaError" });
    return { ok: false, error: failureOf(error, last, failureFields(readFailureFields(last?.body))) };
  }

  /** The Consumer API's test run: a plain request, because its response is the Exchange's answer to a question, not a Curator API contract. */
  async function consumer(path: string, init: { body: string; headers: Record<string, string> }): Promise<Result<TestRunResponse>> {
    let res: Response;
    try {
      const bearer = await token();
      res = await fetchImpl(`${base}${path}`, { method: "POST", body: init.body, headers: { ...init.headers, ...(bearer ? { authorization: `Bearer ${bearer}` } : {}) } });
    } catch {
      return { ok: false, error: { _tag: "NetworkError", status: 0, detail: "Studio couldn't reach the Exchange." } };
    }
    const body: unknown = await res.json().catch(() => null);
    const requestId = res.headers.get("x-request-id");
    const seen: Seen = { status: res.status, ...(requestId ? { requestId } : {}) };
    if (res.ok) {
      const decoded = Schema.decodeUnknownOption(TestRunResponse)(body);
      return Option.isSome(decoded) ? { ok: true, value: decoded.value } : { ok: false, error: failureOf(Option.some({ _tag: "SchemaError" }), seen) };
    }
    return { ok: false, error: failureOf(Option.none(), seen, failureFields(readFailureFields(body))) };
  }

  const withWait = { wait: "1" } as const;
  return {
    hubs: () => curator((api) => api.listHubs()),
    events: async (hub, limit = 200) => {
      const r = await curator((api) => api.listEvents({ query: { hub, limit } }));
      return r.ok ? { ok: true, value: { events: r.value.events.map((e) => ({ ...e, detail: eventDetailOf(e.detail) })) } } : r;
    },
    demand: (hub, days) => curator((api) => api.getDemand({ params: { hub }, query: { days } })),
    golden: () => curator((api) => api.listGolden()),
    register: (repo) => curator((api) => api.registerHub({ payload: { repo } })),
    pushToken: (hub) => curator((api) => api.issuePushToken({ params: { hub }, payload: {} })),
    secrets: (hub) => curator((api) => api.listHubSecrets({ params: { hub } })),
    connectHubToken: (hub) => curator((api) => api.connectHubToken({ params: { hub } })),
    disconnectHubToken: (hub) => curator((api) => api.disconnectHubToken({ params: { hub } })),
    grant: (hub, commit) => curator((api) => api.grantHubSecret({ params: { hub }, payload: { commit } })),
    toolsets: (hub) => curator((api) => api.listToolsets({ params: { hub } })),
    activations: (hub) => curator((api) => api.listActivations({ params: { hub } })),
    stage: (hub, commit) => curator((api) => api.stageToolset({ params: { hub }, query: withWait, payload: { commit } })),
    startRun: (hub, commit, kind) => curator((api) => api.startTestRun({ params: { hub, commit }, query: withWait, payload: { kind } })),
    run: (hub, runId) => curator((api) => api.getTestRun({ params: { hub, runId } })),
    activate: (hub, commit) => curator((api) => api.activateToolset({ params: { hub, commit }, payload: {} })),
    requestRepair: (hub, commit) => curator((api) => api.requestRepair({ params: { hub, commit } })),
    list: (hub) => curator((api) => api.listHub({ params: { hub } })),
    unlist: (hub, reason) => curator((api) => api.unlistHub({ params: { hub }, payload: reason ? { reason } : {} })),
    setAudience: (hub, kind) => curator((api) => api.setHubAudience({ params: { hub }, payload: { kind } })),
    testRun: (question, { hub, bid, staged }) => consumer("/v1/ask", {
      body: JSON.stringify({ questions: [{ text: question, maxCostPerAnswer: bid, hubs: [hub] }] }),
      headers: { "content-type": "application/json", "x-test-run": "1", ...(staged ? { "x-toolsets": "staged" } : {}) },
    }),
    agent: (hub, limit = AGENT_ACTIVITY_LIMIT) => curator((api) => api.getAgentActivity({ params: { hub }, query: { limit } })),
    agentAction: (hub, actionId) => curator((api) => api.getAgentAction({ params: { hub, actionId } })),
    reply: (hub, interactionId, reply) => curator((api) => api.replyToInteraction({ params: { hub, interactionId }, payload: reply })),
    waitingInteractions: () => curator((api) => api.listWaitingInteractions()),
    markRead: (hub, interactionIds) => curator((api) => api.markInteractionsRead({ params: { hub }, payload: { interactionIds } })),
  };
}

/** The statuses the same request may succeed after: a rate limit, and an outage, which say when in Retry-After. */
const RETRYABLE = new Set([429, 503]);

/** A failure in words. `notFound` says what a 404 means for this read, such as "this hub". */
export function describeFailure(e: ApiFailure, notFound?: string): string {
  if (e._tag === "NetworkError") return e.detail ?? "Studio couldn't reach the Exchange.";
  if (e._tag === "ExchangeUnreachable") return `${e.detail ?? "Studio's dev server couldn't reach the Exchange."} Try again in a moment.`;
  if (e.status === 404 && notFound) return `The Exchange doesn't know ${notFound}.`;
  const said = e.detail ?? `The Exchange answered ${e.status}${RETRYABLE.has(e.status) ? ". Try again in a moment" : ""}.`;
  return e.requestId ? `${said} Request ID: ${e.requestId}.` : said;
}
